import { createFileRoute } from "@tanstack/react-router";
import React, { useEffect } from "react";
import { Controller } from "react-hook-form";

import { CountrySelect } from "~/components/country-select";
import { ControlledRadioGroup } from "~/components/form/controlled-inputs";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/cn";
import { useDiscoveryForm } from "~/lib/hooks/use-discovery-form";
import {
  FormOfConductSchema,
  coachLocationCountryField,
  coachLocationRegionField,
  formOfConductStep,
  prefersCoachLocationField,
  sessionDeliveryMethodsFeild,
} from "~/lib/schemas/coach-discovery";
import type { FormOfConductForm } from "~/lib/schemas/coach-discovery";
import { createNoIndexMeta } from "~/lib/shared/meta";

export const Route = createFileRoute("/_public/discovery/steps/7/")({
  head: () =>
    createNoIndexMeta(
      "Step 7: Session Delivery & Location – Dream Coach Match",
      "Set your session delivery method and location preferences in our discovery process",
    ),
  component: DiscoveryStep7,
});

function DiscoveryStep7() {
  const { form, handleNextStep, handlePreviousStep } = useDiscoveryForm({
    step: 7,
    schema: FormOfConductSchema,
  });
  const { control, handleSubmit, watch, setValue, formState } = form;
  const { errors, isSubmitting, isValid } = formState;

  const prefersCoachLocation = watch("prefersCoachLocation");
  const sessionDeliveryMethods = watch("sessionDeliveryMethods");
  const coachLocationCountry = watch("coachLocationCountry");

  const onSubmit = (_data: FormOfConductForm) => {
    handleNextStep();
  };

  // Handle automatic setting of prefersCoachLocation when sessionDeliveryMethods changes
  const previousDeliveryMethod = React.useRef<string | undefined>(undefined);
  const hasInitialized = React.useRef(false);

  useEffect(() => {
    // Wait for form to initialize with saved values
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      previousDeliveryMethod.current = sessionDeliveryMethods;
      return;
    }

    // Only run logic if delivery method actually changed (user interaction)
    if (previousDeliveryMethod.current === sessionDeliveryMethods) {
      return;
    }

    previousDeliveryMethod.current = sessionDeliveryMethods;

    if (
      sessionDeliveryMethods === "in-person" ||
      sessionDeliveryMethods === "hybrid"
    ) {
      if (prefersCoachLocation === "No preference") {
        setValue("prefersCoachLocation", "Yes");
      }
    } else if (sessionDeliveryMethods === "online") {
      if (prefersCoachLocation === "Yes" && !coachLocationCountry) {
        setValue("prefersCoachLocation", "No preference");
      }
    }
  }, [
    sessionDeliveryMethods,
    prefersCoachLocation,
    coachLocationCountry,
    setValue,
  ]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiscoveryStep.Title title={formOfConductStep.title} />
      <DiscoveryStep.Card>
        <FormField
          label={sessionDeliveryMethodsFeild.subTitle || ""}
          labelSize="xl"
          error={errors.sessionDeliveryMethods?.message}
        >
          <ControlledRadioGroup
            control={control}
            name="sessionDeliveryMethods"
            options={
              sessionDeliveryMethodsFeild.options?.map(({ value, label }) => ({
                value,
                label,
              })) || []
            }
            className="flex flex-col gap-y-4"
          />
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Card className="mt-6 p-8">
        <FormField
          labelSize="xl"
          error={errors.prefersCoachLocation?.message}
          label={prefersCoachLocationField.subTitle}
        >
          <div className="mt-4 flex flex-col gap-y-6">
            <ControlledRadioGroup
              control={control}
              name="prefersCoachLocation"
              options={
                prefersCoachLocationField.options?.map(({ value, label }) => ({
                  value,
                  label,
                })) || []
              }
              className="flex flex-col gap-y-6"
            />

            {/* Conditional Location Details */}
            <div
              className={cn(
                "max-h-0 overflow-hidden pl-6 opacity-0 transition-[max-height,opacity] duration-700",
                {
                  "max-h-screen opacity-100":
                    prefersCoachLocation?.includes("Yes"),
                },
              )}
            >
              <div className="mt-4 flex flex-col gap-4">
                <FormField
                  error={errors.coachLocationCountry?.message}
                  label={coachLocationCountryField.subTitle}
                >
                  <Controller
                    name="coachLocationCountry"
                    control={control}
                    render={({ field }) => {
                      const handleChange = (value: string) => {
                        // Only update if value is not empty or if current value is also empty
                        // This prevents the Select from clearing a valid value during country list loading
                        if (value || !field.value) {
                          field.onChange(value);
                        }
                      };
                      return (
                        <CountrySelect
                          value={field.value || ""}
                          onChange={handleChange}
                        />
                      );
                    }}
                  />
                </FormField>

                <FormField
                  label={coachLocationRegionField.subTitle || ""}
                  error={errors.coachLocationRegion?.message}
                >
                  <Controller
                    name="coachLocationRegion"
                    control={control}
                    render={({ field }) => (
                      <Input
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!errors.coachLocationRegion?.message}
                      />
                    )}
                  />
                </FormField>
              </div>
            </div>
          </div>
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Control
        prev={handlePreviousStep}
        type="submit"
        disabled={!isValid}
        loading={isSubmitting}
      />
    </form>
  );
}
